// src/pages/ndr-management/NDRManagement.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { format, subMonths, parseISO, formatISO } from "date-fns";
import { toDate, fromZonedTime } from "date-fns-tz";
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import NDRSearchForm from "../../components/ndr-management/NDRSearchForm";
import NDRTable from "../../components/ndr-management/NDRTable";
import NDRActionModal from "../../components/ndr-management/NDRActionModal";
import NDRDetailsModal from "../../components/ndr-management/NDRDetailsModal";
import NDRCallRecordingModal from "../../components/ndr-management/NDRCallRecordingModal";

const lottieSrc = "/animations/loader.lottie";

const NDR_STATUS_TABS = [
  { key: "100", label: "Pending Action", badgeColor: "bg-yellow-100 text-yellow-600", showCount: true },
  { key: "200", label: "Initiated Action", badgeColor: "bg-green-100 text-green-600", showCount: true },
  { key: "300", label: "Closed", badgeColor: "bg-gray-100 text-gray-600", showCount: false },
];

const NDR_STATUS_MAP = {
  "0": "Unknown Exception", "1": "Customer Unavailable", "2": "Rejected by Customer",
  "3": "Delivery Rescheduled", "4": "No Attempt", "5": "Customer Unreachable",
  "6": "Address Issue", "7": "Payment Issue", "8": "Out Of Delivery Area",
  "9": "Order Already Cancelled", "10": "Self Collect", "11": "Shipment Seized By Customer",
  "12": "Customer wants open delivery", "13": "Shipment Misrouted by logistics partner",
  "14": "OTP-based cancellation", "15": "OTP mismatch or unavailable",
};

const NDR_MANAGEMENT_STATUS_MAP = {
  "100": "Pending Action", "200": "Initiated Action", "300": "Closed", "9999": "Unknown Status",
};

export default function NDRManagement() {
  const defaultFrom = format(subMonths(new Date(), 1), "yyyy-MM-dd'T'HH:mm");
  const defaultTo = format(new Date(), "yyyy-MM-dd'T'HH:mm");

  const [filters, setFilters] = useState({
    orderReferenceNumber: "",
    awbNumber: "",
    ndrStatusCode: "",
    fromDateTime: defaultFrom,
    toDateTime: defaultTo,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortField, setSortField] = useState("ndr_creation_date_time_utc");
  const [sortDirection, setSortDirection] = useState("DESC");

  const [ndrRecords, setNdrRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);

  const [statusCounts, setStatusCounts] = useState(
    Object.fromEntries(NDR_STATUS_TABS.map(t => [t.key, 0]))
  );
  const [selectedTab, setSelectedTab] = useState("100");

  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [callRecordingModalOpen, setCallRecordingModalOpen] = useState(false);
  const [selectedNDR, setSelectedNDR] = useState(null);
  const [selectedActionType, setSelectedActionType] = useState(null);

  // Cross-page selection: Map<orderRefNum, ndrRecordObject> persists across page changes
  const [selectedRows, setSelectedRows] = useState(new Map());
  const headerCheckboxRef = useRef(null);

  // Bulk action states
  const [bulkActionType, setBulkActionType] = useState(null); // "REATTEMPT" | "INITIATE_RTO" | null
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0, successes: 0, failures: [] });

  // Toast state
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  // Toast helper
  const showToast = (message, type = "success") => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ message, type });
    toastTimerRef.current = setTimeout(() => setToast(null), 4000);
  };

  // Selection helpers (cross-page - keyed by order_reference_number, stores full record)
  const rowKey = (ndr) => ndr.order_reference_number;
  const isSelected = (ndr) => selectedRows.has(rowKey(ndr));
  const pageSelectedCount = ndrRecords.filter(isSelected).length;
  const allOnPageSelected = ndrRecords.length > 0 && ndrRecords.every(isSelected);
  const totalSelectedCount = selectedRows.size;

  const toggleSelectOne = (ndr) => {
    setSelectedRows(prev => {
      const next = new Map(prev);
      const key = rowKey(ndr);
      if (next.has(key)) next.delete(key);
      else next.set(key, ndr);
      return next;
    });
  };

  const toggleSelectAllOnPage = (checked) => {
    setSelectedRows(prev => {
      const next = new Map(prev);
      if (checked) ndrRecords.forEach(ndr => next.set(rowKey(ndr), ndr));
      else ndrRecords.forEach(ndr => next.delete(rowKey(ndr)));
      return next;
    });
  };

  const clearSelection = () => setSelectedRows(new Map());

  // Sync header checkbox indeterminate state
  useEffect(() => {
    const cb = headerCheckboxRef.current;
    if (!cb) return;
    cb.indeterminate = pageSelectedCount > 0 && !allOnPageSelected;
  }, [pageSelectedCount, allOnPageSelected, ndrRecords]);

  useEffect(() => {
    fetchNDRData();
  }, [selectedTab]); // Re-fetch when tab changes

  // Convert IST datetime to UTC
  const convertISTtoUTC = (istDateTimeString) => {
    if (!istDateTimeString) return null;
    try {
      // Parse the IST datetime string and convert to UTC
      const istDate = parseISO(istDateTimeString);
      const utcDate = fromZonedTime(istDate, 'Asia/Kolkata');
      return formatISO(utcDate);
    } catch (error) {
      console.error("Error converting IST to UTC:", error);
      return null;
    }
  };

  const fetchNDRData = useCallback(async (overrides = {}) => {
    setLoading(true);
    setError(null);
    try {
      const merchantId = localStorage.getItem("merchantId");
      const authToken = localStorage.getItem("authToken");
      
      if (!authToken) {
        setError("Authentication token not found");
        setLoading(false);
        return;
      }

      // Convert IST to UTC for API
      const fromUTC = convertISTtoUTC(filters.fromDateTime);
      const toUTC = convertISTtoUTC(filters.toDateTime);

      const body = {
        merchant_id: merchantId,
        order_reference_number: filters.orderReferenceNumber || null,
        awb_number: filters.awbNumber || null,
        ndr_management_status_code: selectedTab, // Always filter by selected tab
        ndr_status_code: filters.ndrStatusCode || null,
        order_creation_date_time_utc_from: fromUTC,
        order_creation_date_time_utc_to: toUTC,
        limit: overrides.limit !== undefined ? overrides.limit : limit,
        skip: overrides.skip !== undefined ? overrides.skip : (currentPage - 1) * limit,
        sort_field: overrides.sortField || sortField,
        sort_direction: overrides.sortDirection || sortDirection,
        fetch_only_count: false,
      };

      // Remove null values
      Object.keys(body).forEach(key => {
        if (body[key] === null || body[key] === "") {
          delete body[key];
        }
      });

      const response = await axios.post(
        `${process.env.REACT_APP_OMS_BASE_URL}/v2/oms/ndr/search`,
        body,
        { 
          headers: { 
            Authorization: authToken, 
            "x-merchant-id": merchantId, 
            "Content-Type": "application/json" 
          } 
        }
      );

      setNdrRecords(response.data.ndr_records || []);
      setTotalRecords(response.data.total_count || 0);

      // Fetch counts for tabs that need counts (only Pending and Initiated Action)
      await fetchStatusCounts(authToken, merchantId);
    } catch (err) {
      console.error("Error fetching NDR data:", err);
      setError(err.response?.data?.message || "Failed to fetch NDR records");
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage, limit, sortField, sortDirection, selectedTab]);

  const fetchStatusCounts = async (authToken, merchantId) => {
    try {
      // Convert IST to UTC for API
      const fromUTC = convertISTtoUTC(filters.fromDateTime);
      const toUTC = convertISTtoUTC(filters.toDateTime);

      const counts = {};

      // Fetch count for each status that should show count
      for (const tab of NDR_STATUS_TABS) {
        if (!tab.showCount) {
          // Don't set any count for tabs that shouldn't show count
          continue;
        }

        const body = {
          merchant_id: merchantId,
          ndr_management_status_code: tab.key,
          order_creation_date_time_utc_from: fromUTC,
          order_creation_date_time_utc_to: toUTC,
          fetch_only_count: true,
        };

        Object.keys(body).forEach(key => {
          if (body[key] === null || body[key] === "") {
            delete body[key];
          }
        });

        const response = await axios.post(
          `${process.env.REACT_APP_OMS_BASE_URL}/v2/oms/ndr/search`,
          body,
          { 
            headers: { 
              Authorization: authToken, 
              "x-merchant-id": merchantId, 
              "Content-Type": "application/json" 
            } 
          }
        );

        counts[tab.key] = response.data.total_count || 0;
      }

      setStatusCounts(counts);
    } catch (err) {
      console.error("Error fetching status counts:", err);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchNDRData({ skip: 0 });
  };

  const handleReset = () => {
    const defaultFrom = format(subMonths(new Date(), 1), "yyyy-MM-dd'T'HH:mm");
    const defaultTo = format(new Date(), "yyyy-MM-dd'T'HH:mm");
    
    setFilters({
      orderReferenceNumber: "",
      awbNumber: "",
      ndrStatusCode: "",
      fromDateTime: defaultFrom,
      toDateTime: defaultTo,
    });
    setCurrentPage(1);
    setSelectedTab("100");
  };

  const handleTabChange = (tabKey) => {
    setSelectedTab(tabKey);
    setCurrentPage(1);
    clearSelection();
  };

  const handleLimitChange = (e) => {
    const newLimit = Number(e.target.value);
    setLimit(newLimit);
    setCurrentPage(1);
    fetchNDRData({ limit: newLimit, skip: 0 });
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
      fetchNDRData({ skip: (currentPage - 2) * limit });
    }
  };

  const nextPage = () => {
    if (ndrRecords.length === limit) {
      setCurrentPage(prev => prev + 1);
      fetchNDRData({ skip: currentPage * limit });
    }
  };

  const handleActionClick = (ndr, actionType = null) => {
    setSelectedNDR(ndr);
    setSelectedActionType(actionType);
    setActionModalOpen(true);
  };

  const handleDetailsClick = (ndr) => {
    setSelectedNDR(ndr);
    setDetailsModalOpen(true);
  };

  const handleCallRecordingClick = (ndr) => {
    setSelectedNDR(ndr);
    setCallRecordingModalOpen(true);
  };

  const handleActionSubmit = async (actionData) => {
    try {
      const merchantId = localStorage.getItem("merchantId");
      const authToken = localStorage.getItem("authToken");
      
      if (!authToken) {
        throw new Error("Authentication token not found");
      }

      const response = await axios.put(
        `${process.env.REACT_APP_OMS_BASE_URL}/v2/oms/ndr/action-update`,
        actionData,
        { 
          headers: { 
            Authorization: authToken, 
            "x-merchant-id": merchantId, 
            "Content-Type": "application/json" 
          } 
        }
      );

      // Check if response is successful
      if (response.status === 200 || response.status === 201) {
        // Refresh data in the background
        fetchNDRData();
        // Don't close modal here - let the modal show success and then close
        return;
      } else {
        // Unexpected success status code
        throw new Error("Unexpected response from server");
      }
    } catch (err) {
      console.error("Error updating NDR action:", err);
      // Extract error message from response
      const errorMessage = 
        err.response?.data?.message || 
        err.response?.data?.error || 
        err.message || 
        "Failed to update NDR action. Please try again.";
      
      // Throw error so modal can catch it
      throw new Error(errorMessage);
    }
  };

  // Bulk action: open confirmation
  const handleBulkAction = (actionType) => {
    const pending = [...selectedRows.values()].filter(ndr => ndr.ndr_management_status_code === "100");
    if (pending.length === 0) {
      showToast("No pending-action records in your selection", "error");
      return;
    }
    setBulkActionType(actionType);
    setBulkConfirmOpen(true);
  };

  // Bulk action: execute
  const executeBulkAction = async () => {
    const merchantId = localStorage.getItem("merchantId");
    const authToken = localStorage.getItem("authToken");
    if (!authToken) return;

    const pendingRecords = [...selectedRows.values()].filter(ndr => ndr.ndr_management_status_code === "100");
    setBulkConfirmOpen(false);
    setBulkProcessing(true);
    setBulkProgress({ done: 0, total: pendingRecords.length, successes: 0, failures: [] });

    const actionCode = bulkActionType === "REATTEMPT" ? "100" : "200";
    let successes = 0;
    const failures = [];

    for (let i = 0; i < pendingRecords.length; i++) {
      const ndr = pendingRecords[i];
      const payload = {
        merchant_id: ndr.merchant_id,
        order_reference_number: ndr.order_reference_number,
        ndr_action_type_code: actionCode,
      };

      if (bulkActionType === "REATTEMPT") {
        payload.reattempt_action_details = {
          drop_poc: ndr.delivery_address?.drop_poc || "",
          drop_address: ndr.delivery_address?.drop_address || "",
          drop_city: ndr.delivery_address?.drop_city || "",
          drop_state: ndr.delivery_address?.drop_state || "",
          drop_landmark: ndr.delivery_address?.drop_landmark || null,
          drop_poc_contact_number: ndr.delivery_address?.drop_poc_contact_number || "",
          drop_postal_code: ndr.delivery_address?.drop_postal_code || "",
          drop_country_code: "IN",
          preferred_delivery_date: null,
          internal_remarks: null,
        };
      } else {
        payload.initiate_rto_action_details = { internal_remarks: null };
      }

      try {
        await axios.put(
          `${process.env.REACT_APP_OMS_BASE_URL}/v2/oms/ndr/action-update`,
          payload,
          { headers: { Authorization: authToken, "x-merchant-id": merchantId, "Content-Type": "application/json" } }
        );
        successes++;
      } catch (err) {
        failures.push({
          orderRef: ndr.order_reference_number,
          error: err.response?.data?.message || err.message || "Failed",
        });
      }
      setBulkProgress({ done: i + 1, total: pendingRecords.length, successes, failures: [...failures] });
    }

    setBulkProcessing(false);
    clearSelection();
    fetchNDRData();

    if (failures.length === 0) {
      showToast(`${successes} record(s) updated successfully`, "success");
    } else {
      showToast(`${successes} succeeded, ${failures.length} failed`, "error");
    }
  };

  // CSV download for selected rows
  const handleDownloadCSV = () => {
    const records = [...selectedRows.values()];
    if (records.length === 0) {
      showToast("No rows selected to download", "error");
      return;
    }

    const headers = [
      "Order Reference", "AWB Number", "Carrier", "Order Type",
      "Invoice Value", "Invoice Currency", "Current Order Status",
      "NDR Status", "NDR Management Status", "OFD Attempts",
      "Recipient Name", "Delivery Address", "City", "State", "Postal Code",
      "Contact Number", "NDR Created (UTC)", "Latest NDR Status Date (UTC)",
      "Current Location", "Carrier Remarks",
    ];

    const escapeCSV = (val) => {
      if (val == null) return "";
      const str = String(val);
      if (str.includes(",") || str.includes('"') || str.includes("\n")) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const rows = records.map(ndr => [
      ndr.order_reference_number,
      ndr.awb_number,
      ndr.carrier_partner_public_name,
      ndr.order_type,
      ndr.order_invoice_value,
      ndr.order_invoice_currency,
      ndr.current_order_status_description_external || ndr.current_order_status,
      NDR_STATUS_MAP[ndr.latest_ndr_status_code] || ndr.latest_ndr_status_code,
      NDR_MANAGEMENT_STATUS_MAP[ndr.ndr_management_status_code] || ndr.ndr_management_status_code,
      ndr.out_for_delivery_attempts_count || 0,
      ndr.delivery_address?.drop_poc,
      ndr.delivery_address?.drop_address,
      ndr.delivery_address?.drop_city,
      ndr.delivery_address?.drop_state,
      ndr.delivery_address?.drop_postal_code,
      ndr.delivery_address?.drop_poc_contact_number,
      ndr.ndr_creation_date_time_utc,
      ndr.latest_ndr_status_date_time_utc,
      ndr.current_order_status_current_location,
      ndr.current_order_status_remarks,
    ].map(escapeCSV));

    const csvContent = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ndr_records_${format(new Date(), "yyyyMMdd_HHmmss")}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showToast(`Downloaded ${records.length} record(s)`, "success");
  };

  const totalPages = Math.ceil(totalRecords / limit);
  const pendingInSelection = [...selectedRows.values()].filter(ndr => ndr.ndr_management_status_code === "100").length;
  const isPendingTab = selectedTab === "100";

  return (
    <div className="px-3 sm:px-3 lg:px-3 py-3 max-w-[1800px] mx-auto">

      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-3 mb-6">
        {NDR_STATUS_TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => handleTabChange(tab.key)}
            className={`px-6 py-2.5 rounded-lg font-medium transition-all ${
              selectedTab === tab.key
                ? "bg-blue-600 text-white shadow-md"
                : "bg-white text-gray-700 border hover:bg-gray-50"
            }`}
          >
            <span>{tab.label}</span>
            {tab.showCount && (
              <span
                className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  selectedTab === tab.key
                    ? "bg-white/20 text-white"
                    : tab.badgeColor
                }`}
              >
                {statusCounts[tab.key] || 0}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Search Form */}
      <NDRSearchForm
        filters={filters}
        setFilters={setFilters}
        onSearch={handleSearch}
        onReset={handleReset}
        bulkActions={{
          showBulkReattempt: isPendingTab,
          showBulkRTO: isPendingTab,
          hasPendingSelection: totalSelectedCount > 0 && pendingInSelection > 0,
          hasAnySelection: totalSelectedCount > 0,
          pendingCount: pendingInSelection,
          totalSelected: totalSelectedCount,
          onBulkReattempt: () => handleBulkAction("REATTEMPT"),
          onBulkRTO: () => handleBulkAction("INITIATE_RTO"),
          onDownloadCSV: handleDownloadCSV,
        }}
      />

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-12">
          <DotLottieReact
            src={lottieSrc}
            loop
            autoplay
            style={{ width: 200, height: 200 }}
          />
          <p className="text-gray-600 mt-4">Loading NDR records...</p>
        </div>
      )}

      {/* Results Summary + Bulk Action Buttons */}
      {!loading && (
        <div className="mb-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm text-gray-600 min-w-0">
            <span>Showing {ndrRecords.length} of {totalRecords} records</span>
            {totalSelectedCount > 0 && (
              <>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs font-medium whitespace-nowrap">
                  {totalSelectedCount} selected
                </span>
                <button onClick={clearSelection} className="text-xs text-red-500 hover:text-red-700 underline whitespace-nowrap">
                  Clear
                </button>
              </>
            )}
          </div>

          <div className="flex items-center gap-1.5 flex-shrink-0">
            {isPendingTab && (
              <button
                onClick={() => handleBulkAction("REATTEMPT")}
                disabled={!totalSelectedCount || !pendingInSelection}
                className="h-8 px-3 bg-green-600 text-white text-xs font-semibold rounded-md hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all inline-flex items-center gap-1"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                Reattempt{pendingInSelection > 0 ? ` (${pendingInSelection})` : ""}
              </button>
            )}
            {isPendingTab && (
              <button
                onClick={() => handleBulkAction("INITIATE_RTO")}
                disabled={!totalSelectedCount || !pendingInSelection}
                className="h-8 px-3 bg-orange-600 text-white text-xs font-semibold rounded-md hover:bg-orange-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all inline-flex items-center gap-1"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" /></svg>
                RTO{pendingInSelection > 0 ? ` (${pendingInSelection})` : ""}
              </button>
            )}
            <button
              onClick={handleDownloadCSV}
              disabled={!totalSelectedCount}
              className="h-8 px-3 bg-emerald-600 text-white text-xs font-semibold rounded-md hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all inline-flex items-center gap-1"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
              CSV{totalSelectedCount > 0 ? ` (${totalSelectedCount})` : ""}
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      {!loading && ndrRecords.length > 0 && (
        <NDRTable
          ndrRecords={ndrRecords}
          onActionClick={handleActionClick}
          onDetailsClick={handleDetailsClick}
          onCallRecordingClick={handleCallRecordingClick}
          selectedRows={selectedRows}
          isSelected={isSelected}
          toggleSelectOne={toggleSelectOne}
          toggleSelectAllOnPage={toggleSelectAllOnPage}
          allOnPageSelected={allOnPageSelected}
          headerCheckboxRef={headerCheckboxRef}
        />
      )}

      {/* No Results */}
      {!loading && ndrRecords.length === 0 && !error && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-600 text-lg">No NDR records found</p>
          <p className="text-gray-500 text-sm mt-2">
            Try adjusting your search filters
          </p>
        </div>
      )}

      {/* Pagination */}
      {!loading && ndrRecords.length > 0 && (
        <div className="flex flex-wrap justify-between items-center mt-6">
          <div className="flex items-center space-x-3">
            <button
              onClick={prevPage}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300"
            >
              &larr; Previous
            </button>

            <span className="font-medium">
              Page {currentPage}
              {totalPages > 1 && <span> of {totalPages}</span>}
            </span>

            <button
              onClick={nextPage}
              disabled={ndrRecords.length < limit}
              className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300"
            >
              Next &rarr;
            </button>
          </div>

          <div className="flex items-center space-x-2">
            <label className="font-medium">Rows per page:</label>
            <select
              value={limit}
              onChange={handleLimitChange}
              className="px-3 py-2 border rounded focus:ring-2 focus:ring-blue-400"
            >
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* ===== Bulk Confirmation Modal ===== */}
      {bulkConfirmOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setBulkConfirmOpen(false)}>
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className={`p-5 rounded-t-xl ${bulkActionType === "REATTEMPT" ? "bg-green-50 border-b border-green-200" : "bg-orange-50 border-b border-orange-200"}`}>
              <div className="flex items-center gap-3">
                {bulkActionType === "REATTEMPT" ? (
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                  </div>
                ) : (
                  <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                    <svg className="w-5 h-5 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" /></svg>
                  </div>
                )}
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    Confirm Bulk {bulkActionType === "REATTEMPT" ? "Reattempt" : "Initiate RTO"}
                  </h3>
                  <p className="text-sm text-gray-600">This action cannot be undone</p>
                </div>
              </div>
            </div>
            <div className="p-5">
              <p className="text-sm text-gray-700 mb-4">
                You are about to <strong>{bulkActionType === "REATTEMPT" ? "reattempt delivery" : "initiate RTO"}</strong> for
                <span className="font-bold text-blue-700"> {pendingInSelection} </span>
                pending record{pendingInSelection > 1 ? "s" : ""}.
                {bulkActionType === "REATTEMPT" && (
                  <span className="block mt-2 text-gray-500 text-xs">
                    Existing delivery details (address, contact) will be used. To modify details, use individual actions instead.
                  </span>
                )}
                {bulkActionType === "INITIATE_RTO" && (
                  <span className="block mt-2 text-amber-600 text-xs font-medium">
                    All selected shipments will be sent back to the origin address.
                  </span>
                )}
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setBulkConfirmOpen(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium text-sm transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={executeBulkAction}
                  className={`px-5 py-2 text-white rounded-lg font-semibold text-sm transition-colors ${
                    bulkActionType === "REATTEMPT" ? "bg-green-600 hover:bg-green-700" : "bg-orange-600 hover:bg-orange-700"
                  }`}
                >
                  Yes, Proceed
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== Bulk Processing Progress Modal ===== */}
      {bulkProcessing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              Processing Bulk Action...
            </h3>

            {/* Progress bar */}
            <div className="mb-4">
              <div className="flex justify-between text-sm text-gray-600 mb-1.5">
                <span>{bulkProgress.done} of {bulkProgress.total} processed</span>
                <span>{bulkProgress.total > 0 ? Math.round((bulkProgress.done / bulkProgress.total) * 100) : 0}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${bulkProgress.total > 0 ? (bulkProgress.done / bulkProgress.total) * 100 : 0}%` }}
                />
              </div>
            </div>

            {/* Stats */}
            <div className="flex gap-4 text-sm">
              <div className="flex items-center gap-1.5 text-green-700">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                {bulkProgress.successes} succeeded
              </div>
              {bulkProgress.failures.length > 0 && (
                <div className="flex items-center gap-1.5 text-red-700">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  {bulkProgress.failures.length} failed
                </div>
              )}
            </div>

            {/* Failure details (scrollable) */}
            {bulkProgress.failures.length > 0 && (
              <div className="mt-3 max-h-32 overflow-y-auto bg-red-50 rounded-lg p-3 text-xs space-y-1">
                {bulkProgress.failures.map((f, i) => (
                  <div key={i} className="text-red-700">
                    <span className="font-medium">{f.orderRef}:</span> {f.error}
                  </div>
                ))}
              </div>
            )}

            <p className="text-xs text-gray-400 mt-4">Please don't close this page while processing...</p>
          </div>
        </div>
      )}

      {/* Action Modal */}
      {actionModalOpen && selectedNDR && (
        <NDRActionModal
          ndr={selectedNDR}
          preSelectedAction={selectedActionType}
          onClose={() => {
            setActionModalOpen(false);
            setSelectedNDR(null);
            setSelectedActionType(null);
          }}
          onSubmit={handleActionSubmit}
        />
      )}

      {/* Details Modal */}
      {detailsModalOpen && selectedNDR && (
        <NDRDetailsModal
          ndr={selectedNDR}
          onClose={() => {
            setDetailsModalOpen(false);
            setSelectedNDR(null);
          }}
          onActionClick={handleActionClick}
        />
      )}

      {/* Call Recording Modal */}
      {callRecordingModalOpen && selectedNDR && (
        <NDRCallRecordingModal
          ndr={selectedNDR}
          onClose={() => {
            setCallRecordingModalOpen(false);
            setSelectedNDR(null);
          }}
        />
      )}

      {/* ===== Toast Notification ===== */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 animate-slide-down">
          <div className={`flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-sm font-medium ${
            toast.type === "success" ? "bg-green-600 text-white" : "bg-red-600 text-white"
          }`}>
            {toast.type === "success" ? (
              <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            ) : (
              <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            )}
            <span>{toast.message}</span>
            <button onClick={() => setToast(null)} className="ml-2 opacity-70 hover:opacity-100">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-slide-down { animation: slideDown 0.25s ease-out; }
      `}</style>
    </div>
  );
}